import React, { useContext, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useAccount from "./hooks/useAccount.hook";
import { ACCOUNT_FIRST_TIME_URL, ENTITY_TYPE_AGENCY, ENTITY_TYPE_VENDOR, WELCOME_URL, HOME_URL } from "./utlities/core.constants";
import isActiveAccount from "./utlities/accountStatus";
import { UserContext } from "./pages/UserContext";
import useEntity from "./hooks/useEntity.hook";
import usePrimaryPoc from "./hooks/usePrimaryPoc.hook";
import useDap from "./hooks/useDap.hook";

export default function AppShell() {
  const { token, setJwt, setUser, setRequest, setUserEntity, setAuthError, checkFalsyPocFields, setInitialUserStorageCheck, setInitialUserCallbackCheck, isImpersonating } = useContext(UserContext);
  const { getUserVendor, getUserAgency } = useEntity();
  const { getAppUser, getUserAccessRequest } = useAccount();
  const { getPrimaryPoc } = usePrimaryPoc();
  const navigate = useNavigate();
  const location = useLocation();
  useDap();

  useEffect(() => {
    if (!location.pathname.includes('/callback')) {
      setInitialUserCallbackCheck(true);
    }
  }, [location]);

  useEffect(() => {
    (async () => {
      if (!token) {
        return;
      }

      const appUserResponse = await getAppUser();
      if (appUserResponse.status === 403) {
        const inactiveAccountMessage = "You are trying to log in with an inactive ACR Repository account. Please contact your organization's administrator to reactivate your account.";
        const blockedLoginMessage = "Your login is currently blocked. Please contact your account administrator for assistance.";
        const authBlockMessage =
          appUserResponse.data?.code === "ACCOUNT_INACTIVE"
            ? inactiveAccountMessage
            : blockedLoginMessage;

        setAuthError(authBlockMessage);
        setJwt(null);
        navigate(HOME_URL, { replace: true });
        setInitialUserStorageCheck(true);
        return;
      }
      if (appUserResponse.status !== 200) {
        setJwt(null);
        navigate(HOME_URL, { replace: true });
        setInitialUserStorageCheck(true);
        return;
      }
      const user = appUserResponse.data;
      const userAccessRequest = await getUserAccessRequest();

      setUser(user);
      setRequest(userAccessRequest);

      if (!isActiveAccount(user)) {
        navigate(userAccessRequest ? WELCOME_URL : ACCOUNT_FIRST_TIME_URL);
        setInitialUserStorageCheck(true);
        return;
      }

      let entity = null;

      // While impersonating, the acting org (and its userEntity) is the impersonated org, which
      // UserContext hydrates from the active session. Skip hydrating the real user's own org here so
      // this per-navigation effect can't clobber the impersonated entity with the SuperUser's org.
      if (!isImpersonating) {
        // Check if user is a vendor user first
        if (user.vendorId) {
          const vendorResponse = await getUserVendor();

          if (vendorResponse && vendorResponse.id) {
            entity = { type: ENTITY_TYPE_VENDOR, entity: vendorResponse };

            if (vendorResponse && vendorResponse.primaryPocId) {
              let primaryPoc = await getPrimaryPoc(vendorResponse.primaryPocId)
              primaryPoc = checkFalsyPocFields(primaryPoc);
              entity = { ...entity, primaryPoc };
            }

            setUserEntity(entity);
          }
        }
        // Otherwise check if user is an agency user
        else if (user.agencyId) {
          const agencyResponse = await getUserAgency();
          if (agencyResponse && agencyResponse.id) {
            entity = { type: ENTITY_TYPE_AGENCY, entity: agencyResponse };

            if (agencyResponse && agencyResponse.primaryPocId) {
              let primaryPoc = await getPrimaryPoc(agencyResponse.primaryPocId)
              primaryPoc = checkFalsyPocFields(primaryPoc);

              entity = { ...entity, primaryPoc };
            }
            setUserEntity(entity);
          }
        }
      }

      setInitialUserStorageCheck(true);
    })();
  }, [token, location.pathname, isImpersonating])

  return <Outlet />
}
